/**
 * User Operations Handler
 */

import { supabase } from '../../supabase';
import { ParsedCommand, ExecutionResult } from '../types';

export async function executeUserOperation(
  command: ParsedCommand,
  userId: string
): Promise<ExecutionResult> {
  const { intent, entities } = command;

  try {
    switch (intent) {
      case 'lookup_user':
        return await lookupUser(entities.target_user, entities.target_email, entities.target_id);

      case 'query_users':
        return await queryUsers(entities.filters);

      case 'update_user':
        if (!entities.target_id && !entities.target_email && !entities.target_user) {
          return {
            success: false,
            error: 'No user identifier provided',
            message: 'Please specify which user to update',
          };
        }
        return await updateUser(entities, userId);

      default:
        return {
          success: false,
          error: `Unsupported user operation: ${intent}`,
          message: 'Operation not supported',
        };
    }
  } catch (error) {
    console.error('Error in user operation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      message: 'Failed to execute user operation',
    };
  }
}

async function lookupUser(
  targetName?: string,
  targetEmail?: string,
  targetId?: string
): Promise<ExecutionResult> {
  let query = supabase
    .from('profiles')
    .select(`
      id,
      full_name,
      phone_number,
      created_at,
      plan,
      is_system_manager,
      home_manager,
      lifecycle_status,
      lifecycle_updated_at,
      lifecycle_notes,
      notes
    `);

  if (targetId) {
    query = query.eq('id', targetId);
  } else if (targetEmail) {
    // For email lookup, we need to query a different way since email is in auth.users
    // For now, we'll search by name if provided
    if (targetName) {
      query = query.ilike('full_name', `%${targetName}%`);
    } else {
      return {
        success: false,
        error: 'Cannot search by email directly (limitation)',
        message: 'Please provide the user\'s name or ID instead',
      };
    }
  } else if (targetName) {
    query = query.ilike('full_name', `%${targetName}%`);
  } else {
    return {
      success: false,
      error: 'No user identifier provided',
      message: 'Please specify a user name, email, or ID',
    };
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  if (!data || data.length === 0) {
    return {
      success: false,
      error: 'User not found',
      message: 'No user found matching that criteria',
    };
  }

  if (data.length > 1) {
    return {
      success: true,
      data,
      message: `Found ${data.length} users matching "${targetName || targetEmail}"`,
      affectedRecords: data.length,
      recordDetails: data,
    };
  }

  return {
    success: true,
    data: data[0],
    message: `Found user: ${data[0].full_name}`,
    affectedRecords: 1,
    recordDetails: data,
  };
}

async function queryUsers(filters?: Record<string, any>): Promise<ExecutionResult> {
  let query = supabase
    .from('profiles')
    .select(`
      id,
      full_name,
      phone_number,
      created_at,
      plan,
      is_system_manager,
      home_manager,
      lifecycle_status,
      lifecycle_updated_at
    `)
    .order('created_at', { ascending: false });

  // Apply filters
  if (filters) {
    if (filters.lifecycle_status) {
      query = query.eq('lifecycle_status', filters.lifecycle_status);
    }

    if (filters.home_manager) {
      query = query.eq('home_manager', filters.home_manager);
    }

    if (filters.plan) {
      const { data: homes, error: homesError } = await supabase
        .from('homes')
        .select('owner_id')
        .eq('plan', filters.plan)
        .not('owner_id', 'is', null);

      if (homesError) throw homesError;

      const ownerIds = Array.from(new Set((homes || []).map((home) => home.owner_id).filter(Boolean)));
      if (ownerIds.length === 0) {
        return {
          success: true,
          data: [],
          message: 'Found 0 users',
          affectedRecords: 0,
          recordDetails: [],
        };
      }
      query = query.in('id', ownerIds);
    }

    if (filters.is_system_manager !== undefined) {
      query = query.eq('is_system_manager', filters.is_system_manager);
    }
  }

  const { data, error } = await query.limit(100);

  if (error) {
    throw error;
  }

  return {
    success: true,
    data,
    message: `Found ${data?.length || 0} user${data?.length !== 1 ? 's' : ''}`,
    affectedRecords: data?.length || 0,
    recordDetails: data,
  };
}

async function updateUser(
  entities: Record<string, any>,
  executorUserId: string
): Promise<ExecutionResult> {
  const { target_id, target_user, target_email, updates } = entities;

  if (!updates || Object.keys(updates).length === 0) {
    return {
      success: false,
      error: 'No updates provided',
      message: 'Please specify what to update',
    };
  }

  // First, find the user
  let userId: string | undefined = target_id;

  if (!userId) {
    // Look up by name
    const lookupResult = await lookupUser(target_user, target_email, target_id);
    if (!lookupResult.success || !lookupResult.data) {
      return lookupResult;
    }

    if (Array.isArray(lookupResult.data)) {
      if (lookupResult.data.length > 1) {
        return {
          success: false,
          error: 'Multiple users found',
          message: 'Multiple users match that name. Please be more specific or use the user ID.',
        };
      }
      userId = lookupResult.data[0].id;
    } else {
      userId = lookupResult.data.id;
    }
  }

  // Fetch current record
  const { data: current, error: fetchError } = await supabase
    .from('profiles')
    .select('id, full_name, lifecycle_status, home_manager')
    .eq('id', userId)
    .single();

  if (fetchError || !current) {
    return {
      success: false,
      error: 'User not found',
      message: `User not found`,
    };
  }

  // Add metadata for lifecycle and home manager changes
  const updateData: Record<string, any> = { ...updates };

  if (updates.lifecycle_status) {
    updateData.lifecycle_updated_at = new Date().toISOString();
  }

  if (updates.home_manager) {
    updateData.home_manager_assigned_at = new Date().toISOString();
    updateData.home_manager_assigned_by = executorUserId;

    // Keep per-home manager assignments in sync.
    const { error: homesUpdateError } = await supabase
      .from('homes')
      .update({
        home_manager: updates.home_manager,
        home_manager_assigned_at: updateData.home_manager_assigned_at,
        home_manager_assigned_by: executorUserId,
      })
      .eq('owner_id', userId);

    if (homesUpdateError) {
      throw homesUpdateError;
    }
  }

  // Perform update
  const { data, error } = await supabase
    .from('profiles')
    .update(updateData)
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return {
    success: true,
    data,
    message: `Successfully updated user "${current.full_name}"`,
    affectedRecords: 1,
    recordDetails: [data],
  };
}
